import type React from 'react';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import { InstitutionFuturesProgressStep, InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useInstitutionDetailsLocked, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE, type InstitutionDetailsKeyForDetailsType, type InstitutionDetailsType } from '@/features/institution/utils';
import BaseLayout from '@/layouts/base';

type InstitutionDetailsLayoutProps<T extends InstitutionDetailsType = InstitutionDetailsType> = React.ComponentPropsWithoutRef<typeof BaseLayout> & {
  detailsKey: InstitutionDetailsKeyForDetailsType<T>;
  detailsType: T;
};

const INSTITUTION_DETAILS_PROGRESS_STEP_MAP = {
  [INSTITUTION_DETAILS_TYPE.ONBOARDING]: InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
  [INSTITUTION_DETAILS_TYPE.FUTURES]: InstitutionFuturesProgressStep.INSTITUTION_INFORMATION_FUTURES,
} as const;

const InstitutionDetailsLayout = <T extends InstitutionDetailsType>({ detailsKey, detailsType, ...props }: InstitutionDetailsLayoutProps<T>) => {
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { data: version } = useInstitutionDetailsVersion();
  const { fullyLockedKeys } = useInstitutionDetailsLocked();
  const { setCurrentStep } = useProgress();
  const isFullyLocked = fullyLockedKeys.some((key) => key === detailsKey);

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(INSTITUTION_DETAILS_PROGRESS_STEP_MAP[detailsType], getDetailsKeyProgress(detailsType, version, detailsKey));
  }, [detailsKey, detailsType, setCurrentStep, version]);

  // Changes to a fully locked section would be discarded on save
  if (isFullyLocked) return <Navigate to={`${onboardingPath}/review`} replace />;

  return <BaseLayout {...props} />;
};
InstitutionDetailsLayout.displayName = 'InstitutionDetailsLayout';

export default InstitutionDetailsLayout;
